<template>
  <div class="kk-designer__grid-topleft" :style="style"></div>
</template>

<script>
import { ColumnLetterHeight, EditModes, HotkeyScopes, RowNumberWidth } from "../../../utils/constants";
import DesignerMixin from "../DesignerMixin";
import hotkeys from "hotkeys-js";
import { getCellId } from "./Utils";
import { ProcessTypes } from "../../../utils/icons";
import { indexToColumn, indexToRow, isRangeSelectionAllowed } from "../../../utils/util";
import { rangeSelection } from "../../../store/Cells";
import { ActiveCorners } from "../corner/CornerConstants";
import { ColumnTypes } from "./CellConstants";
import { rangeIndexToLabel } from "../../process-design/cell/Utils";
import { stateHistory } from "../../../store/state-history";
export default {
  mixins: [DesignerMixin],
  props: ["getPxFromRowIndex", "getPxFromColumnIndex"],
  data() {
    return {
      ActiveCorners: ActiveCorners,
    };
  },
  computed: {
    showAddNamedRangeDialog() {
      return this.$store.state.table.addNamedRangeVisibility;
    },
    showDialog() {
      return this.$store.state.table.showAddTableDialog;
    },
    error() {
      return this.$store.state.pdesign.error?.summary;
    },
    shouldScroll() {
      return this.$store.state.cells.shouldScroll;
    },
    editMode() {
      return this.$store.state.pdesign.editMode.value;
    },
    style() {
      const border = this.current.type !== ProcessTypes.Grid ? { border: "none" } : {};
      return {
        height: ColumnLetterHeight + "px",
        width: RowNumberWidth + "px",
        ...border,
      };
    },
    activeRangeMainCellCoordinate() {
      return this.$store.state.cells.activeRangeMainCellCoordinate;
    },
    editCell() {
      return this.$store.state.cells.editCell;
    },
    selectedArrow() {
      return this.$store.state.cells.selectedArrow;
    },
    activeCornerMode() {
      return this.$store.state.views.activeCorner;
    },
    contentFocused() {
      return this.$store.state.pdesign.contentFocused;
    },
    ctrlPressed() {
      return this.$store.getters["rangeMovement/ctrlPressed"];
    },
  },
  watch: {
    shouldScroll() {
      this.scroll("activeCellId");
    },
  },
  mounted() {
    hotkeys("*", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (
        !this.mainRange?.range ||
        event.key.length !== 1 ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        (event.shiftKey && event.code === "Space")
      )
        return;
      if (this.editMode === EditModes.Disable) {
        event.preventDefault();
        this.$store.dispatch("pdesign/setEditMode", EditModes.OuterEdit);
        this.$store.dispatch("cells/setEditCell", {
          text: event.key,
          range: "",
          cursorPosition: 1,
          formulaRangeSelectionAllowed: isRangeSelectionAllowed(event.key),
        });
      }
    });
    hotkeys("cmd+z,ctrl+z", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event.preventDefault();
      if (stateHistory.canUndo && !stateHistory.busy) stateHistory.undo();
    });
    hotkeys("cmd+y,ctrl+y", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event.preventDefault();
      if (stateHistory.canRedo && !stateHistory.busy) stateHistory.redo();
    });
    hotkeys("ctrl+p,cmd+p", async (event, handler) => {
      event.preventDefault();
      this.$store.dispatch("smartSearch/searchAndCall", { value: "ScreenShot", i18n: this.$i18n });
    });

    hotkeys("ctrl+g,cmd+g", (event) => {
      event.preventDefault();
      this.$store.dispatch("pdesign/setGotoBox", true);
    });

    hotkeys("f2", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (this.editMode === EditModes.InnerEdit) {
        this.$store.dispatch("pdesign/setEditMode", EditModes.OuterEdit);
      } else {
        this.$store.dispatch("pdesign/setEditMode", EditModes.InnerEdit);
      }
    });
    hotkeys("ctrl+t, cmd+t", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.mainRange?.range) return;
      event?.preventDefault();
      if (this.current.type === ProcessTypes.Grid) {
        await this.$store.dispatch("table/loadTables", false);
        const rangeValue = this.$store.getters["cells/activeRange"]().range;

        const isRangeConflict = this.$store.getters["table/tableRangeConflict"]({
          range: rangeValue,
          table: null,
        });
        const activeTable = this.$store.state.table.activeTable;
        if (!isRangeConflict || activeTable) {
          this.$store.dispatch("table/setShowAddTableDialog", true);
        }
      }
    });
    hotkeys("ctrl+f3, cmd+f3", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.mainRange?.range) return;
      event?.preventDefault();
      if (this.current.type === ProcessTypes.Grid) {
        this.$store.dispatch("table/setNameManagerVisibility", true);
      }
    });
    hotkeys("ctrl+shift+f3, cmd+shift+f3", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.mainRange?.range) return;
      event?.preventDefault();
      if (this.current.type === ProcessTypes.Grid) {
        this.$store.dispatch("table/setAddNamedRangeVisibility", true);
      }
    });

    hotkeys("ctrl+f2, cmd+f2", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.mainRange?.range) return;
      event?.preventDefault();
      this.$store.dispatch("pdesign/toggleFormulaBox");
    });

    hotkeys("up", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let [startColIdx] = this.activeRangeMainCellCoordinate;
      let [, startRowIdx] = this.calculateRange(this.activeRangeMainCellCoordinate);

      if (startRowIdx < 1) return;

      startRowIdx = this.$store.getters["rangeMovement/getNextVerticalCell"](startRowIdx, -1);
      const row = this.rows[startRowIdx];
      const column = this.columns[startColIdx];
      const activeCellId = getCellId(row.id, column.id);

      const rangeLabel = rangeIndexToLabel(this.getRangeByCellId(activeCellId));

      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [startColIdx, startRowIdx]);

      this.updateMovement(rangeLabel, activeCellId);
    });
    hotkeys("down", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let [startColIdx] = this.activeRangeMainCellCoordinate;
      let [, , , endRowIdx] = this.calculateRange(this.activeRangeMainCellCoordinate);

      if (endRowIdx + 1 >= this.rows.length) return;

      endRowIdx = this.$store.getters["rangeMovement/getNextVerticalCell"](endRowIdx, 1);

      const row = this.rows[endRowIdx];
      const column = this.columns[startColIdx];
      const activeCellId = getCellId(row.id, column.id);

      const rangeLabel = rangeIndexToLabel(this.getRangeByCellId(activeCellId));

      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [startColIdx, endRowIdx]);

      this.updateMovement(rangeLabel, activeCellId);
    });
    hotkeys("right", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let [, startRowIdx] = this.activeRangeMainCellCoordinate;
      let [, , endColIdx] = this.calculateRange(this.activeRangeMainCellCoordinate);

      if (endColIdx + 1 >= this.columns.length) return;
      endColIdx = this.$store.getters["rangeMovement/getNextHorizontalCell"](endColIdx, 1);
      const row = this.rows[startRowIdx];
      const column = this.columns[endColIdx];
      const activeCellId = getCellId(row.id, column.id);
      const rangeLabel = rangeIndexToLabel(this.getRangeByCellId(activeCellId));
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [endColIdx, startRowIdx]);
      this.updateMovement(rangeLabel, activeCellId);
    });
    hotkeys("left", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let [, startRowIdx] = this.activeRangeMainCellCoordinate;
      let [startColIdx] = this.calculateRange(this.activeRangeMainCellCoordinate);

      if (startColIdx - 1 < 0) return;

      startColIdx = this.$store.getters["rangeMovement/getNextHorizontalCell"](startColIdx, -1);
      const row = this.rows[startRowIdx];
      const column = this.columns[startColIdx];
      const activeCellId = getCellId(row.id, column.id);
      const rangeLabel = rangeIndexToLabel(this.getRangeByCellId(activeCellId));

      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [startColIdx, startRowIdx]);

      this.updateMovement(rangeLabel, activeCellId);
    });

    hotkeys("ctrl+left,cmd+left", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;
      this.ctrlArrowFunctionality(-1);
    });
    hotkeys("ctrl+right,cmd+right", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;
      this.ctrlArrowFunctionality(1);
    });

    hotkeys("ctrl+up,cmd+up", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.activeCellId) return;

      const range = this.$store.getters["cells/activeRange"]().range;
      const rowIds = this.$store.getters["cells/rowsInRange"](range);
      const rowsLength = rowIds.length;
      if (this.selectedArrow) return this.$store.dispatch("cells/moveRowArrow", -1);

      let sourceIndex = this.rowMap[rowIds[0]].index;

      const contextMenuExists = document.querySelector(".kk-designer__activity-menu-wrapper.kk-designer__vertical");
      if (!contextMenuExists || sourceIndex <= this.firstVisibleRowIndex) return this.setNextActiveRowCell(-1);

      const targetIndex = this.$store.getters["rangeMovement/getNextVerticalCell"](sourceIndex, -1);
      const changeCount = await this.$store.dispatch("cells/moveActivity", {
        sourceIndex,
        targetIndex,
        rowIds,
      });
      if (changeCount) {
        await this.$store.dispatch("rangeMovement/moveRangeSelection", { direction: "up" });
        this.scroll("up");
      }
    });
    hotkeys("ctrl+down,cmd+down", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      if (!this.activeCellId) return;

      const range = this.$store.getters["cells/activeRange"]().range;
      const rowIds = this.$store.getters["cells/rowsInRange"](range);
      const rowsLength = rowIds.length;

      if (this.selectedArrow) return this.$store.dispatch("cells/moveRowArrow", 1);

      let sourceIndex = this.rowMap[rowIds[0]].index;

      const contextMenuExists = document.querySelector(".kk-designer__activity-menu-wrapper.kk-designer__vertical");
      if (!contextMenuExists || sourceIndex + 1 > this.rows.length - 1) return this.setNextActiveRowCell(1);

      const targetIndex = this.$store.getters["rangeMovement/getNextVerticalCell"](sourceIndex, 1);
      const changeCount = await this.$store.dispatch("cells/moveActivity", {
        sourceIndex,
        targetIndex,
        rowIds,
      });

      if (changeCount) {
        this.$store.dispatch("rangeMovement/moveRangeSelection", { direction: "down" });
        this.scroll("down");
      }
    });

    hotkeys("ctrl+d,cmd+d", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      event.preventDefault();
      const [startColumn, startRow, endColumn, endRow] = this.mainRange.range;

      // should select single cell or single row
      if (!isRange || (isRange && endRow - startRow === 0)) {
        const initialRange = [startColumn, startRow - 1, endColumn, endRow - 1];

        this.$store.dispatch("cells/fill", { initialRange, newRange: this.mainRange.range });
      } else this.$store.dispatch("cells/fillDirection", { range: this.mainRange.range, direction: "down" });
    });
    hotkeys("ctrl+shift+d,cmd+shift+d", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (!isRange) return;

      event.preventDefault();

      this.$store.dispatch("cells/fillSeries", { range: this.mainRange.range, seriesIn: "Columns", type: "AutoFill" });
    });
    hotkeys("ctrl+r,cmd+r", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      event.preventDefault();
      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      const [startColumn, startRow, endColumn, endRow] = this.mainRange.range;
      // should select single cell or single column
      if (!isRange || (isRange && endColumn - startColumn === 0)) {
        const initialRange = [startColumn - 1, startRow, endColumn - 1, endRow];

        this.$store.dispatch("cells/fill", { initialRange, newRange: this.mainRange.range });
      } else this.$store.dispatch("cells/fillDirection", { range: this.mainRange.range, direction: "right" });
    });
    hotkeys("ctrl+shift+r,cmd+shift+r", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (!isRange) return;

      event.preventDefault();
      this.$store.dispatch("cells/fillSeries", { range: this.mainRange.range, seriesIn: "Rows", type: "AutoFill" });
    });
    hotkeys("ctrl+a,cmd+a", HotkeyScopes.ProcessDesigner, (event, handler) => {
      let [startColumnIndex, startRowIndex, endColumnIndex, endRowIndex] =
        this.$store.getters["cells/activeRange"]().range;
      const startColumnId = this.columns[startColumnIndex].id;
      const startRowId = this.rows[startRowIndex].id;

      if (this.current.type === ProcessTypes.Grid) {
        const activeTable = this.$store.getters["table/activeTable"](`${startRowId}_${startColumnId}`);

        if (activeTable) {
          const range = this.$store.getters["table/dataRange"](activeTable);
          startColumnIndex = range[0];
          startRowIndex = range[1];
          endColumnIndex = range[2];
          endRowIndex = range[3];
        } else {
          startColumnIndex = 0;
          startRowIndex = 0;
          endColumnIndex = this.columns.length - 1;
          endRowIndex = this.rows.length - 1;
        }
      } else {
        startColumnIndex = this.columns.findIndex((x) => x.type === "player");
        startRowIndex = this.rows.findIndex((x) => x.type === "activity");
        endColumnIndex = this.columns.length - 1;
        endRowIndex = this.rows.length - 1;
      }

      const range = rangeIndexToLabel([startColumnIndex, startRowIndex, endColumnIndex, endRowIndex]);
      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel: range, activeRangeId });
    });
    hotkeys("shift+right", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;
      let scrollArg = null;

      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;

      let range = this.getRangeById(activeRangeId);

      if (["left", "single"].includes(this.getMainCellHorizontalPosition(range))) {
        range = await this.$store.dispatch("rangeMovement/increaseRight", range);
        scrollArg = "right";
      } else {
        scrollArg = "left";
        range = await this.$store.dispatch("rangeMovement/decreaseLeft", range);
      }
      const rangeLabel = rangeIndexToLabel(range);
      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel, activeRangeId });
      this.scroll(scrollArg);
      this.setCtrlPressed(false);
    });
    hotkeys("shift+left", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      let scrollArg = null;
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      let range = this.getRangeById(activeRangeId);

      if (["right", "single"].includes(this.getMainCellHorizontalPosition(range))) {
        range = await this.$store.dispatch("rangeMovement/increaseLeft", range);
        scrollArg = "left";
      } else {
        range = await this.$store.dispatch("rangeMovement/decreaseRight", range);
        scrollArg = "right";
      }

      const rangeLabel = rangeIndexToLabel(range);

      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel, activeRangeId });
      this.scroll(scrollArg);
      this.setCtrlPressed(false);
    });
    hotkeys("shift+down", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;
      let scrollArg = null;

      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      let range = this.getRangeById(activeRangeId);

      if (["top", "single"].includes(this.getMainCellVerticalPosition(range))) {
        scrollArg = "down";
        range = await this.$store.dispatch("rangeMovement/increaseBottom", range);
      } else {
        scrollArg = "up";
        range = await this.$store.dispatch("rangeMovement/decreaseTop", range);
      }

      const rangeLabel = rangeIndexToLabel(range);

      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel, activeRangeId });
      this.scroll(scrollArg);
      this.setCtrlPressed(false);
    });
    hotkeys("shift+up", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let scrollArg = null;
      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      let range = this.getRangeById(activeRangeId);

      if (["bottom", "single"].includes(this.getMainCellVerticalPosition(range))) {
        range = await this.$store.dispatch("rangeMovement/increaseTop", range);
        scrollArg = "up";
      } else {
        scrollArg = "down";
        range = await this.$store.dispatch("rangeMovement/decreaseBottom", range);
      }

      const rangeLabel = rangeIndexToLabel(range);

      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel, activeRangeId });
      this.scroll(scrollArg);
      this.setCtrlPressed(false);
    });
    hotkeys("cmd+shift+up,ctrl+shift+up", HotkeyScopes.ProcessDesigner, (event) => {
      event?.preventDefault();
      this.setCtrlPressed(true);
      hotkeys.trigger("shift+up", HotkeyScopes.ProcessDesigner);
    });

    hotkeys("cmd+shift+down,ctrl+shift+down", HotkeyScopes.ProcessDesigner, (event) => {
      event?.preventDefault();
      this.setCtrlPressed(true);
      hotkeys.trigger("shift+down", HotkeyScopes.ProcessDesigner);
    });

    hotkeys("cmd+shift+right,ctrl+shift+right", HotkeyScopes.ProcessDesigner, (event) => {
      event?.preventDefault();
      this.setCtrlPressed(true);
      hotkeys.trigger("shift+right", HotkeyScopes.ProcessDesigner);
    });

    hotkeys("cmd+shift+left,ctrl+shift+left", HotkeyScopes.ProcessDesigner, (event) => {
      event?.preventDefault();
      this.setCtrlPressed(true);
      hotkeys.trigger("shift+left", HotkeyScopes.ProcessDesigner);
    });
    hotkeys("ctrl+space, cmd+space", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;
      let [startColumnIndex, startRowIndex, endColumnIndex] = this.$store.getters["cells/activeRange"]().range;
      const startColumnId = this.columns[startColumnIndex].id;
      const endColumnId = this.columns[endColumnIndex].id;
      const startRowId = this.rows[startRowIndex].id;
      if (
        this.current.type === ProcessTypes.Posting &&
        [ColumnTypes.Player, ColumnTypes.Grid].includes(this.columns[startColumnIndex].type) &&
        [ColumnTypes.Player, ColumnTypes.Grid].includes(this.columns[endColumnIndex].type)
      ) {
        const isStartMinus = /-minus$/i.test(startColumnId);
        const isEndMinus = /-minus$/i.test(endColumnId);

        startColumnIndex = isStartMinus ? startColumnIndex - 1 : startColumnIndex;
        endColumnIndex = isEndMinus ? endColumnIndex : endColumnIndex + 1;
      }

      startRowIndex = 0;
      let endRowIndex = this.rows.length - 1;
      const activeTable = this.$store.getters["table/activeTable"](`${startRowId}_${startColumnId}`);

      if (activeTable) {
        const range = this.$store.getters["table/dataRange"](activeTable);
        startRowIndex = range[1];
        endRowIndex = range[3];
      }

      const selectedColumn = `${indexToColumn(startColumnIndex)}${indexToRow(startRowIndex)}:${indexToColumn(
        endColumnIndex
      )}${indexToRow(endRowIndex)}`;
      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel: selectedColumn, activeRangeId });
    });
    hotkeys("shift+space", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      let [startColumnIndex, startRowIndex, , endRowIndex] = this.$store.getters["cells/activeRange"]().range;
      const startRowId = this.rows[startRowIndex].id;
      const startColumnId = this.columns[startColumnIndex].id;

      startColumnIndex = 0;
      let endColumnIndex = this.columns.length - 1;
      const activeTable = this.$store.getters["table/activeTable"](`${startRowId}_${startColumnId}`);

      if (activeTable) {
        const range = this.$store.getters["table/dataRange"](activeTable);
        startColumnIndex = range[0];
        endColumnIndex = range[2];
      }
      const selectedRow = `${indexToColumn(startColumnIndex)}${indexToRow(startRowIndex)}:${indexToColumn(
        endColumnIndex
      )}${indexToRow(endRowIndex)}`;

      const activeRangeId = this.rangeDragging.id || rangeSelection.activeRangeId;
      this.$store.dispatch("rangeMovement/updateShiftMovement", { rangeLabel: selectedRow, activeRangeId });
    });

    hotkeys("shift+f2", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      const id = this.activeCellId;
      if (this.cellsMetaData[id]?.comment) {
        this.$store.dispatch("cells/setComment", { id, editMode: true });
      } else {
        this.$store.dispatch("cells/addComment");
      }
    });

    hotkeys("tab", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (this.current.type === ProcessTypes.Grid && !isRange) {
        hotkeys.trigger("right", HotkeyScopes.ProcessDesigner);
      }
      if (isRange) {
        this.$store.dispatch("cells/incrementActiveCellIndexInRange", { columnDelta: 1 });
        this.scroll("activeCellId");
      } else this.$store.dispatch("cells/selectArrow", 1);
    });
    hotkeys("shift+tab", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (this.current.type === ProcessTypes.Grid && !isRange) {
        hotkeys.trigger("left", HotkeyScopes.ProcessDesigner);
      }
      if (isRange) {
        this.$store.dispatch("cells/incrementActiveCellIndexInRange", { columnDelta: -1 });
        this.scroll("activeCellId");
      } else this.$store.dispatch("cells/selectArrow", -1);
    });
    hotkeys("enter", HotkeyScopes.ProcessDesigner, async (event, handler) => {
      event?.preventDefault();
      if (this.showDialog || this.showAddNamedRangeDialog) {
        this.$store.dispatch("cells/setStartDragging", false);
        this.$store.dispatch("cells/setRangeSelectorCollapse", true);
        return;
      }
      if (!this.mainRange?.range || this.rangeDragging?.drag === "table") return;
      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (isRange) {
        this.$store.dispatch("cells/incrementActiveCellIndexInRange", { rowDelta: 1 });
      } else {
        await this.$store.dispatch("cells/setEditCell", { range: "", formulaRangeSelectionAllowed: false });
        await this.$store.dispatch("cells/setActiveRangeId", this.mainRange.id);
        this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.activeCellId));
        if (!this.error) {
          hotkeys.trigger("down", HotkeyScopes.ProcessDesigner);
        }
      }
      this.scroll("activeCellId");
    });
    hotkeys("shift+enter", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      if (!this.mainRange?.range) return;

      const isRange = this.$store.getters["cells/isRange"](this.mainRange.range);
      if (isRange) {
        this.$store.dispatch("cells/incrementActiveCellIndexInRange", { rowDelta: -1 });
      } else hotkeys.trigger("up", HotkeyScopes.ProcessDesigner);
      this.scroll("activeCellId");
    });
    hotkeys("ctrl+.,cmd+.", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      this.$store.dispatch("cells/toggleCornerCellInRange");
    });

    hotkeys("ctrl+c,cmd+c", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      this.$store.dispatch("cells/copyContent", { isCut: false });
    });
    hotkeys("ctrl+x,cmd+x", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      this.$store.dispatch("cells/copyContent", { isCut: true });
    });
    hotkeys("ctrl+v,cmd+v", HotkeyScopes.ProcessDesigner, (event, handler) => {
      if (!this.mainRange?.range) return;
      this.$store.dispatch("cells/pasteContent");
    });
    // Toggle scopes between sidebar and design area.
    hotkeys("ctrl+\\,cmd+\\", HotkeyScopes.All, (event, handler) => {
      event.preventDefault();
      const activePanel = this.preferences.allPreferences.activeSubNavigation;
      const newScope = hotkeys.getScope() === HotkeyScopes.ProcessDesigner ? activePanel : HotkeyScopes.ProcessDesigner;
      this.$store.dispatch("setScope", newScope);
    });

    hotkeys("escape, delete", HotkeyScopes.ProcessDesigner, (event, handler) => {
      event?.preventDefault();
      switch (handler.key) {
        case "escape":
          this.$store.dispatch("cells/resetSelectedArrow");
          this.$store.dispatch("cells/resetArrow");
          break;
        case "delete": {
          if (this.selectedArrow) return this.$store.dispatch("cells/deleteSelectedArrow");
          if (!this.mainRange?.range) return;
          this.$store.dispatch("cells/deleteSelectedCells");
          this.$store.dispatch("cells/setEditCell", { text: "" });
          this.$store.dispatch("cells/clearFormulaMetaData", this.mainRange.range);
          //delete table if it's in range
          this.$store.dispatch("table/deleteTableAndHeaderInRange", this.mainRange.range);
        }
      }
    });
    hotkeys("ctrl+q", HotkeyScopes.All, (event, handler) => {
      event?.preventDefault();
      this.$store.dispatch("views/setActiveCorner", this.activeCornerMode === 1 ? 0 : 1);
    });
    hotkeys("ctrl+f11, cmd+f11", HotkeyScopes.All, (event, handler) => {
      event?.preventDefault();
      this.$store.dispatch("pdesign/setContentFocus", !this.contentFocused);
    });
  },
  beforeUnmount() {
    hotkeys.unbind("ctrl+q,ctrl+\\,ctrl+g,cmd+g,ctrl+p,cmd+p,ctrl+f11, cmd+f11", HotkeyScopes.All);
    hotkeys.unbind(
      "*,up, down, right, left, ctrl+f2, ctrl+up,cmd+up, ctrl+down, cmd+down, ctrl+right, cmd+right, ctrl+left,cmd+left,ctrl+r,cmd+r,ctrl+d,cmd+d, ctrl+a,cmd+a, shift+right, ctrl+shift+right, shift+left, ctrl+shift+left, shift+down, ctrl+shift+down, shift+up, ctrl+shift+up, ctrl+space,cmd+space, shift+space, shift+f2, tab, shift+tab, enter, shift+enter, ctrl+., cmd+., ctrl+c, cmd+c , ctrl+x, cmd+x , ctrl+v, cmd+v, escape, delete , f2 , ctrl+p, cmd+p,f4,ctrl+t, cmd+t , ctrl+z , cmd+z, ctrl+y, cmd+y",
      HotkeyScopes.ProcessDesigner
    );
  },
  methods: {
    setCtrlPressed(isPressed) {
      this.$store.dispatch("rangeMovement/setCtrlPressed", isPressed);
    },
    ctrlArrowFunctionality(moveDirection) {
      // 1 for right -1 for left
      if (!moveDirection) return;
      const isRightKey = moveDirection === 1;
      const isLeftKey = moveDirection === -1;

      const range = this.$store.getters["cells/activeRange"]().range;
      const columnIds = this.$store.getters["cells/columnsInRange"](range);

      const moveCell = async () => {
        const contextMenuExists = document.querySelector(".kk-designer__activity-menu-wrapper.kk-designer__horizontal");
        if (!contextMenuExists) return this.setNextActiveColumnCell(moveDirection);

        let sourceIndex = this.columnMap[columnIds[0]].index;
        const shiftCount =
          this.current.type === ProcessTypes.Posting && this.columns[sourceIndex].type === "player" ? 2 : 1;

        let targetIndex =
          this.current.type === ProcessTypes.Posting && this.columns[sourceIndex].type === "player"
            ? this.$store.getters["rangeMovement/getNextHorizontalCell"](sourceIndex + moveDirection, moveDirection)
            : this.$store.getters["rangeMovement/getNextHorizontalCell"](sourceIndex, moveDirection);
        if (
          (isRightKey && sourceIndex >= this.columns.length - columnIds.length) ||
          (isLeftKey && targetIndex <= 0) ||
          sourceIndex === targetIndex
        )
          return;

        const changeCount = await this.$store.dispatch("cells/movePlayer", {
          sourceIndex,
          targetIndex,
          count: columnIds.length,
        });
        if (changeCount) {
          this.$store.dispatch("rangeMovement/moveRangeSelection", {
            direction: isRightKey ? "right" : "left",
            shiftCount,
          });
          this.scroll(isRightKey ? "right" : "left");
        }
      };

      const moveArrow = async () => {
        let arrow = this.design.arrows[this.selectedArrow.rowId].find((x) => x.id === this.selectedArrow.id);
        let [startPlayerIdx, endPlayerIdx] = isRightKey
          ? this.moveRight(
              this.players.findIndex((item) => item.id === arrow.startPlayerId),
              this.players.findIndex((item) => item.id === arrow.endPlayerId)
            )
          : this.moveLeft(
              this.players.findIndex((item) => item.id === arrow.startPlayerId),
              this.players.findIndex((item) => item.id === arrow.endPlayerId)
            );

        arrow = {
          ...arrow,
          rowId: this.selectedArrow.rowId,
          startPlayerId: this.players[startPlayerIdx].id,
          endPlayerId: this.players[endPlayerIdx].id,
        };

        await this.$store.dispatch("cells/updateArrow", arrow);
      };

      if (this.selectedArrow) {
        moveArrow();
      } else {
        moveCell();
      }
    },
    async setNextActiveRowCell(delta) {
      await this.$store.dispatch("rangeMovement/setNextActiveRowCell", delta);
      this.scroll();
    },
    async setNextActiveColumnCell(delta) {
      await this.$store.dispatch("rangeMovement/setNextActiveColumnCell", delta);
      this.scroll();
    },
    scroll(direction) {
      // direction = which of the four sides is moving (increasing or decreasing)
      let rowIndex, columnIndex;
      if (direction === "activeCellId") {
        [columnIndex, rowIndex] = this.getCellIndexById(this.activeCellId);
      } else if (direction === "left") {
        [columnIndex, , , rowIndex] = this.$store.getters["cells/activeRange"]().range;
      } else if (direction === "up") {
        [, rowIndex, columnIndex] = this.$store.getters["cells/activeRange"]().range;
      } else {
        [, , columnIndex, rowIndex] = this.$store.getters["cells/activeRange"]().range;
      }
      const rowId = this.rows[rowIndex]?.id;
      const columnId = this.columns[columnIndex]?.id;
      if (!rowId || !columnId) return;

      const container = document.querySelector(".table-content");
      const containerBound = container.getBoundingClientRect();

      const columnLeft =
        this.getPxFromColumnIndex(columnIndex) + RowNumberWidth + containerBound.left - container.scrollLeft;
      const columnRight = columnLeft + this.getRawWidth(columnId);
      const rowTop = this.getPxFromRowIndex(rowIndex) + ColumnLetterHeight + containerBound.top - container.scrollTop;
      const rowBottom = rowTop + this.getRawHeight(rowId);

      const movementOnXLine = ["left", "right", undefined, "activeCellId"].includes(direction);
      const movementOnYLine = ["up", "down", undefined, "activeCellId"].includes(direction);

      if (columnRight > window.innerWidth && movementOnXLine) {
        const diff = columnRight - window.innerWidth + 30;
        container.scrollLeft += diff;
      }
      if (columnLeft < containerBound.left + RowNumberWidth && movementOnXLine) {
        const diff = containerBound.left + RowNumberWidth - columnLeft + 30;
        container.scrollLeft -= diff;
      }

      if (rowBottom > window.innerHeight && movementOnYLine) {
        const diff = rowBottom - window.innerHeight + 15;
        container.scrollTop += diff;
      }
      if (rowTop < containerBound.top + ColumnLetterHeight && movementOnYLine) {
        const diff = containerBound.top + ColumnLetterHeight - rowTop + 15;
        container.scrollTop -= diff;
      }
    },
    updateMovement(rangeLabel, activeCellId) {
      this.$store.dispatch("rangeMovement/updateMovement", { rangeLabel, activeCellId });
      this.scroll();
    },
    getRangeById(rangeId) {
      return this.$store.getters["rangeMovement/getRangeById"](rangeId);
    },
    getMainCellHorizontalPosition(range) {
      if (range[0] === range[2]) return "single"; // single column
      if (range[0] === this.activeRangeMainCellCoordinate[0]) return "left";
      return "right";
    },
    getMainCellVerticalPosition(range) {
      if (range[1] === range[3]) return "single"; // single row
      if (range[1] === this.activeRangeMainCellCoordinate[1]) return "top";
      return "bottom";
    },
    moveRight(startPlayerIdx, endPlayerIdx) {
      this.selectedArrow?.arrowHead ? endPlayerIdx++ : startPlayerIdx++;

      if (startPlayerIdx > this.players.length - 1) startPlayerIdx--;
      if (endPlayerIdx > this.players.length - 1) endPlayerIdx--;

      if (this.players[startPlayerIdx].id.includes("Plus") && this.players[endPlayerIdx].id.includes("Minus")) {
        if (this.selectedArrow?.arrowHead) {
          endPlayerIdx < this.players.length - 1 ? endPlayerIdx++ : endPlayerIdx--;
        } else {
          startPlayerIdx < this.players.length - 1 ? startPlayerIdx++ : startPlayerIdx--;
        }
      }

      return [startPlayerIdx, endPlayerIdx];
    },
    moveLeft(startPlayerIdx, endPlayerIdx) {
      this.selectedArrow?.arrowHead ? endPlayerIdx-- : startPlayerIdx--;

      if (startPlayerIdx < 0) startPlayerIdx++;
      if (endPlayerIdx < 0) endPlayerIdx++;

      if (this.players[startPlayerIdx].id.includes("Plus") && this.players[endPlayerIdx].id.includes("Minus")) {
        if (this.selectedArrow?.arrowHead) {
          endPlayerIdx > 0 ? endPlayerIdx-- : endPlayerIdx++;
        } else {
          startPlayerIdx > 0 ? startPlayerIdx-- : startPlayerIdx++;
        }
      }

      return [startPlayerIdx, endPlayerIdx];
    },
  },
};
</script>

<style>
.kk-designer__grid-topleft {
  background: #f8f8fb;
  border-right: 1px solid #eee;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  left: 0;
  grid-area: 1/1;
  z-index: 95;
}
</style>
